import { logger } from "@/utils/logger";
import { MessagePart } from './types';
import { processInventoryHydration } from '../vehicleHydration';
import { getVehicleCardByStyleId } from '../showroomUtils';

// Helper to normalize text for comparison (removes accents and converts to lowercase)
const normalizeForComparison = (text: string): string => {
  return text
    .normalize('NFD') // Decompose combined characters
    .replace(/[\u0300-\u036f]/g, '') // Remove diacritics
    .toLowerCase()
    .trim();
};

export const shouldSkipReplyOptions = (replyMessage: any, jsonData?: any): boolean => {
  // Check for special trade-in estimate reply options first - this overrides everything
  const replyOptions = replyMessage?.Reply_Options || replyMessage?.Content?.Reply_Options || 
    jsonData?.Reply_Options || jsonData?.Content?.Reply_Options;
  
  if (replyOptions && Array.isArray(replyOptions)) {
    const hasTradeEstimateOption = replyOptions.some((option: string) => {
      const normalized = normalizeForComparison(option);
      return (
        (normalized.includes('estime') && normalized.includes('30sec')) ||
        (normalized.includes('online estimate') && normalized.includes('30sec'))
      );
    });
    
    if (hasTradeEstimateOption) {
      return false; // Don't skip - show the reply options regardless of form or other content
    }
  }
  
  // Check if Form object has a meaningful Type - only skip for actual forms
  const formData = jsonData?.Form || replyMessage?.Form;
  const validFormTypes = ['Contact', 'Offers', 'FinanceV1', 'Service', 'Appointment'];
  const hasMeaningfulForm = formData?.Type && validFormTypes.includes(formData.Type);
  
  if (hasMeaningfulForm) {
    return true;
  }

  const content = replyMessage?.Content && typeof replyMessage.Content === 'object' ? replyMessage.Content : {};

  // Support both Reply_Message.Content.Inventory and Reply_Message.Inventory
  const openingHours = content?.Opening_Hours ?? replyMessage?.Opening_Hours;
  const inventory = content?.Inventory ?? replyMessage?.Inventory;
  const vehicleCount = content?.Vehicle_Count ?? replyMessage?.Vehicle_Count;
  const promotions = content?.Promotions ?? replyMessage?.Promotions;

  const hasOpeningHours = Array.isArray(openingHours) && openingHours.length > 0;

  // Check if inventory has at least 1 card - if so, skip reply options
  const hasInventoryCards = Array.isArray(inventory) && inventory.length > 0;
  
  // Check if showroom has meaningful content (not just empty objects)
  if (content?.Showroom && typeof content.Showroom === 'object') {
    const showroom = content.Showroom;
    const hasMeaningfulButtons = 
      (showroom.VDP?.Button_Text && showroom.VDP?.Link) ||
      (showroom.Build_And_Price?.Button_Text && showroom.Build_And_Price?.Link) ||
      (showroom.Brochure?.Button_Text && showroom.Brochure?.Link) ||
      (showroom.Youtube_Video?.Link); // YouTube only needs Link, Button_Text is optional
    const hasSuggestedModels = showroom.Suggested_Models && Array.isArray(showroom.Suggested_Models) && showroom.Suggested_Models.length > 0;
    
    if (hasMeaningfulButtons || hasSuggestedModels) {
      return true;
    }
  }
  
  // Check if Vehicle_Count exists - if so, skip reply options to show inventory count
  const hasVehicleCount = Array.isArray(vehicleCount) && vehicleCount.length > 0;
  
  // Check if Promotions exist - if so, skip reply options to show promotions
  const hasPromotions = Array.isArray(promotions) && promotions.length > 0;
  
  return hasOpeningHours || hasInventoryCards || hasVehicleCount || hasPromotions;
};

export const processReplyOptions = (replyMessage: any, jsonData: any): MessagePart | null => {
  const replyOptions = replyMessage.Reply_Options || replyMessage.Content?.Reply_Options || 
    jsonData.Reply_Options || jsonData.Content?.Reply_Options;
  
  if (replyOptions && Array.isArray(replyOptions)) {
    const nonEmptyOptions = replyOptions.filter((option: string) => 
      option && typeof option === 'string' && option.trim().length > 0
    );
    
    if (nonEmptyOptions.length > 0) {
      return { type: 'reply_options', content: { Reply_Options: nonEmptyOptions } };
    }
  }
  
  return null;
};

export const processInventoryOrVehicleCount = async (replyMessage: any): Promise<MessagePart | null> => {
  if (!replyMessage || typeof replyMessage !== 'object') {
    return null;
  }

  // Normalize: sometimes the AI returns Inventory at Reply_Message.Inventory instead of Reply_Message.Content.Inventory
  if (!replyMessage.Content || typeof replyMessage.Content !== 'object') {
    replyMessage.Content = {};
  }
  if (!replyMessage.Content.Inventory && Array.isArray(replyMessage.Inventory)) {
    logger.log('📋 Normalizing Reply_Message.Inventory to Reply_Message.Content.Inventory');
    replyMessage.Content.Inventory = replyMessage.Inventory;
  }

  // Priority 1: Always use vehicles list view regardless of count
  if (replyMessage.Content.Inventory && Array.isArray(replyMessage.Content.Inventory) && replyMessage.Content.Inventory.length > 0) {
    
    // CRITICAL: Hydrate VIN-only vehicles from inventory-index.json if inventory is loaded
    // This works for ALL dealers that have inventory_load=true
    const hydratedInventory = await processInventoryHydration(replyMessage.Content.Inventory);

    logger.log('🚗 HYDRATION COMPLETE:', {
      hydratedCount: hydratedInventory.length,
      firstHydratedItem: hydratedInventory[0] ? {
        Make: hydratedInventory[0].Make,
        Model: hydratedInventory[0].Model,
        VIN: hydratedInventory[0].VIN,
        Price: hydratedInventory[0].Price
      } : null
    });

    // Filter out inventory items that don't have any meaningful vehicle data
    const meaningfulInventory = hydratedInventory.filter((vehicle: any) => {
      if (!vehicle || typeof vehicle !== 'object') return false;

      const hasMake = typeof vehicle.Make === 'string' && vehicle.Make.trim().length > 0;
      const hasModel = typeof vehicle.Model === 'string' && vehicle.Model.trim().length > 0;
      const hasYear = vehicle.Year !== undefined && String(vehicle.Year).trim().length > 0;
      const hasPrice = typeof vehicle.Price === 'number' && vehicle.Price > 0;
      const hasVin = typeof vehicle.VIN === 'string' && vehicle.VIN.trim().length > 0;
      const hasVdpUrl = typeof vehicle.VDP_URL === 'string' && vehicle.VDP_URL.trim().length > 0;

      const isMeaningful = hasMake || hasModel || hasYear || hasPrice || hasVin || hasVdpUrl;
      
      if (!isMeaningful) {
        logger.warn('⚠️ Filtered out vehicle with no meaningful data:', vehicle);
      }
      
      return isMeaningful;
    });

    logger.log('🚗 FILTERING COMPLETE:', {
      meaningfulCount: meaningfulInventory.length,
      filteredOut: hydratedInventory.length - meaningfulInventory.length
    });

    if (meaningfulInventory.length === 0) {
      console.error('❌ CRITICAL: Inventory exists but contains no meaningful vehicle data after hydration!');
      console.error('Original inventory:', replyMessage.Content.Inventory);
      console.error('Hydrated inventory:', hydratedInventory);
      return null;
    }
    
    // Sort inventory by price in ascending order before mapping
    const sortedInventory = [...meaningfulInventory].sort((a, b) => {
      const priceA = a.Price ?? Infinity; // Treat undefined/null prices as highest
      const priceB = b.Price ?? Infinity;
      return priceA - priceB;
    });
    
    const vehiclesList = sortedInventory.map((vehicle: any) => ({
      Main_Picture: vehicle.Main_Picture_URL,
      Year: vehicle.Year,
      Make: vehicle.Make,
      Model: vehicle.Model,
      Trim: vehicle.Trim,
      Color: vehicle.Exterior_Color,
      Mileage: vehicle.Mileage,
      Price: vehicle.Price,
      VDP_URL: vehicle.VDP_URL,
      Vehicle_Description_Summary: vehicle.Vehicle_Description_Summary,
      Features: vehicle.Features,
      Stock_Number: vehicle.Stock_Number,
      VIN: vehicle.VIN,
      Fuel_Type: vehicle.Fuel_Type,
      Status: vehicle.Status || vehicle.status, // Include Status for payment filtering
      // Only use LowestPayment if it's not a URL
      LowestPayment: vehicle.LowestPayment && !vehicle.LowestPayment.includes('http') && !vehicle.LowestPayment.includes('://') 
        ? vehicle.LowestPayment 
        : undefined,
      LowestPaymentTerms: vehicle.LowestPaymentTerms
    }));
    
    logger.log('🚗💳 VehiclesList with Status:', vehiclesList.map(v => ({ VIN: v.VIN, Status: v.Status })));
    return { type: 'vehicles_list', content: vehiclesList };
  }

  // Priority 2: Suggested models (but NEVER from showroom - showroom handles its own suggested models)
  const suggestedModels = replyMessage.Content.Suggested_Models;
  
  // Check if a showroom object exists at all (even with empty values)
  const showroom = replyMessage.Content.Showroom;
  const hasShowroomObject = showroom && typeof showroom === 'object';
  
  // If showroom object exists, it will handle rendering its own suggested models
  // So only use suggested models that are NOT in a showroom
  const finalSuggestedModels = !hasShowroomObject ? suggestedModels : null;
  
  if (finalSuggestedModels && Array.isArray(finalSuggestedModels) && finalSuggestedModels.length > 0) {
    // Hydrate models using styleid - preserve only lowestPayments, lowestPaymentsTerms, youtube_id
        const hydratedModels = await Promise.all(finalSuggestedModels.map(async model => {
      // If model has styleid, look it up and ignore all other fields except the preserved ones
      const styleIdValue = model.showroom_styleId || model.styleid || model.styleId;
      if (styleIdValue) {
        const vehicleCard = await getVehicleCardByStyleId(styleIdValue);
        if (vehicleCard) {
          // Map showroom fields to SuggestedModelItem format, preserving only specific fields from original
          return {
            showroom_styleId: styleIdValue,
            styleid: styleIdValue,
            Main_Picture_URL: vehicleCard.mainPicture || '',
            Year: vehicleCard.year,
            Make: vehicleCard.make,
            Model: vehicleCard.model,
            Trim: vehicleCard.trim,
            Fuel_Type: vehicleCard.drivetrain || '',
            Price: vehicleCard.finalPrice || 0,
            VDP_URL: vehicleCard.showroomUrlEnglish || vehicleCard.showroomUrlFrench || '',
            Build_And_Price: (vehicleCard.buildAndPriceUrlEnglish || vehicleCard.buildAndPriceUrlFrench) ? {
              Button_Text: `Build & Price ${vehicleCard.model || ''} ${vehicleCard.trim || ''}`.trim(),
              Link: vehicleCard.buildAndPriceUrlEnglish || vehicleCard.buildAndPriceUrlFrench || ''
            } : undefined,
            Youtube_Video: undefined,
            LowestPayment: undefined,
            LowestPaymentTerms: undefined,
            // Preserve brochure metadata from AI response so UI can fall back when showroom JSON lacks it
            Brochure: model.Brochure,
            // Preserve these fields from the original model object
            lowestPayments: model.lowestPayments,
            lowestPaymentsTerms: model.lowestPaymentsTerms,
            youtube_id: model.youtube_id
          };
        }
        // If showroom lookup failed, fall back to AI-provided fields
        return model;
      }
      // Model doesn't have styleid, return as-is
      return model;
    }));
    
    const filteredModels = hydratedModels.filter(model => model !== null);
    
    // Filter out empty or invalid vehicles - allow through if has any useful display field
    const validModels = filteredModels.filter(model =>
      model && 
      (model.Make && model.Make.trim()) && 
      (model.Model && model.Model.trim()) && 
      (model.Main_Picture_URL || model.VDP_URL || model.Price > 0)
    );
    
    if (validModels.length > 0) {
      return { type: 'suggested_models', content: validModels };
    }
  }

  // Priority 3: Vehicle count (inventory breakdown) - only if no inventory exists
  if (replyMessage.Content.Vehicle_Count && Array.isArray(replyMessage.Content.Vehicle_Count) && replyMessage.Content.Vehicle_Count.length > 0) {
    // Skip Vehicle_Count if Inventory exists (even if empty array, as it indicates inventory data structure is present)
    const hasInventoryStructure = replyMessage.Content.Inventory !== undefined;
    
    if (!hasInventoryStructure) {
      return { 
        type: 'inventory_count', 
        content: replyMessage.Content.Vehicle_Count 
      };
    }
  }
  
  return null;
};

export const processPromotions = (replyMessage: any): MessagePart | null => {
  if (!replyMessage?.Content?.Promotions || !Array.isArray(replyMessage.Content.Promotions)) {
    return null;
  }
  
  // Filter out empty strings, null values, and invalid promotion objects
  // Also normalize case (AI may return Title or title)
  const validPromotions = replyMessage.Content.Promotions.filter((promo: any) => {
    // Filter out empty strings, null, or undefined
    if (!promo || typeof promo === 'string' && promo.trim() === '') {
      return false;
    }
    
    // If it's an object, ensure it has at least a title (case-insensitive check)
    if (typeof promo === 'object' && !promo.title && !promo.Title) {
      return false;
    }
    
    return true;
  }).map((promo: any) => {
    // Normalize PascalCase keys to lowercase for consistent rendering
    if (typeof promo === 'object') {
      return {
        title: promo.title || promo.Title || '',
        link: promo.link || promo.Link || '',
        description: promo.description || promo.Description || '',
        details: promo.details || promo.Details || '',
        image: promo.image || promo.Image || '',
        type: promo.type || promo.Type || ''
      };
    }
    return promo;
  });
  
  if (validPromotions.length === 0) {
    return null;
  }
  
  return { 
    type: 'promotions_list', 
    content: validPromotions 
  };
};

// Helper to check if a department has valid opening hours data
const isValidDepartmentHours = (dept: any): boolean => {
  // Must have a non-empty department name
  if (!dept.Department || typeof dept.Department !== 'string' || dept.Department.trim() === '') {
    return false;
  }
  
  // Must have at least one day with actual opening/closing times
  if (!dept.Days || !Array.isArray(dept.Days) || dept.Days.length === 0) {
    return false;
  }
  
  // Check if at least one day has valid times
  const hasValidDay = dept.Days.some((day: any) => {
    const hasOpening = day.opening && typeof day.opening === 'string' && day.opening.trim() !== '';
    const hasClosing = day.closing && typeof day.closing === 'string' && day.closing.trim() !== '';
    return hasOpening || hasClosing;
  });
  
  return hasValidDay;
};

export const processOpeningHoursOrServiceForm = (replyMessage: any, jsonData: any): MessagePart[] => {
  // Filter out empty/invalid opening hours entries
  const rawOpeningHours = replyMessage.Content?.Opening_Hours;
  const validOpeningHours = Array.isArray(rawOpeningHours) 
    ? rawOpeningHours.filter(isValidDepartmentHours)
    : [];
  
  const hasOpeningHours = validOpeningHours.length > 0;
  
  if (!hasOpeningHours) {
    // Even if no opening hours, still trigger service form if it exists
    const hasForm = jsonData.Form?.Type === 'Service' || jsonData.Form?.Type === 'Appointment';
    if (hasForm) {
      return [{ 
        type: 'service_form', 
        content: { 
          form: jsonData.Form.Type,
          FormTitle: jsonData.Form.Title || jsonData.FormTitle
        } 
      }];
    }
    
    return [];
  }
  
  const parts: MessagePart[] = [];
  
  // Always show opening hours in the message when they're present
  parts.push({ 
    type: 'opening_hours', 
    content: validOpeningHours 
  });
  
  // Also trigger form activation if there's a Service/Appointment form
  const hasForm = jsonData.Form?.Type === 'Service' || jsonData.Form?.Type === 'Appointment';
  
  if (hasForm) {
    parts.push({ 
      type: 'service_form', 
      content: { 
        form: jsonData.Form.Type,
        FormTitle: jsonData.Form.Title || jsonData.FormTitle,
        openingHours: validOpeningHours 
      }
    });
  }
  
  return parts;
};